import { cn } from "@sagot/ui";
import { useId } from "react";
import { edgePath, targetPoint, type Box, type CanvasEdge, type Point } from "./canvas-layout";
import { handlePoint, triggerPoint, type TileRow } from "./connector-geometry";
import type { PointerBindings } from "./use-connector-drag";

const ARROW_SIZE = 8;
const GRIP_RADIUS = 8;
/** The grip sits just outside the target tile, on the arrowhead. */
const GRIP_GAP = 14;
const HIT_WIDTH = 12;
const DASH = "6 4";
const FLAGGED_DASH = "5 4";

/** The arrowheads: one per look of a connector, since a marker cannot take the colour of the line it ends. */
const HEADS = { rest: "fill-fg-subtle", active: "fill-primary-ink", flagged: "fill-warning" } as const;
type Head = keyof typeof HEADS;

interface CanvasEdgesProps {
  edges: readonly CanvasEdge[];
  boxes: Readonly<Record<string, Box>>;
  rows: Readonly<Record<string, readonly TileRow[]>>;
  width: number;
  height: number;
  selected: string | null;
  /** The connector of the exit whose handle has keyboard focus: highlighted too, without a grip. */
  focused: string | null;
  /** Paths with a step the server flagged: connectors into them are dashed warnings. */
  flaggedPaths: ReadonlySet<string>;
  /** The connector whose grip is being dragged stays (so the grip keeps the pointer) but fades: the preview line shows where it is going. */
  dimmed: string | null;
  preview: { from: Point; to: Point } | null;
  editable: boolean;
  grip: (edge: CanvasEdge) => PointerBindings;
  onSelect: (edge: CanvasEdge) => void;
}

function startOf(edge: CanvasEdge, boxes: Readonly<Record<string, Box>>, rows: Readonly<Record<string, readonly TileRow[]>>): Point | null {
  const from = boxes[edge.from];
  if (!from) return null;
  if (edge.key === null) return triggerPoint(from);
  return handlePoint(from, rows[edge.from] ?? [], edge.step, edge.key);
}

/**
 * Connectors between tiles, one per connected exit, and the line that follows the pointer while one is dragged. The
 * drawing is hidden from assistive technology (every exit's handle is the accessible control); the wide invisible
 * stroke under each line is only there so a mouse can click a connector.
 */
export function CanvasEdges({ edges, boxes, rows, width, height, selected, focused, flaggedPaths, dimmed, preview, editable, grip, onSelect }: CanvasEdgesProps) {
  const prefix = useId();
  const head = (kind: Head) => `url(#${prefix}-${kind})`;
  return (
    <svg className="pointer-events-none absolute left-0 top-0 overflow-visible" width={width} height={height} aria-hidden="true" focusable="false">
      <defs>
        {(Object.keys(HEADS) as Head[]).map((kind) => (
          <marker key={kind} id={`${prefix}-${kind}`} markerWidth={ARROW_SIZE} markerHeight={ARROW_SIZE} refX={ARROW_SIZE - 1} refY={ARROW_SIZE / 2} orient="auto" markerUnits="userSpaceOnUse">
            <path d={`M 0 0 L ${String(ARROW_SIZE)} ${String(ARROW_SIZE / 2)} L 0 ${String(ARROW_SIZE)} z`} className={HEADS[kind]} />
          </marker>
        ))}
      </defs>
      {edges.map((edge) => {
        const to = boxes[edge.to];
        const start = startOf(edge, boxes, rows);
        if (!to || !start) return null;
        const d = edgePath(start, targetPoint(to));
        const chosen = edge.id === selected;
        const lit = chosen || edge.id === focused;
        const flagged = flaggedPaths.has(edge.to);
        const kind: Head = lit ? "active" : flagged ? "flagged" : "rest";
        return (
          <g key={edge.id} className={cn("group", edge.id === dimmed && "opacity-40")}>
            <path
              d={d}
              className={cn("fill-none", lit ? "stroke-primary-ink [stroke-width:2.5]" : flagged ? "stroke-warning" : "stroke-fg-subtle group-hover:stroke-primary-ink group-hover:[stroke-width:2.5]")}
              strokeWidth={1.5}
              strokeDasharray={flagged && !lit ? FLAGGED_DASH : undefined}
              markerEnd={head(kind)}
              data-testid="canvas-edge"
              data-from={edge.from}
              data-to={edge.to}
              data-exit={edge.key ?? undefined}
              data-fixed={edge.fixed ? "true" : undefined}
              data-selected={chosen ? "true" : undefined}
            />
            {edge.key === null || edge.fixed ? null : (
              // The handle on the exit is the keyboard and screen-reader control; this stroke only widens the mouse target.
              <path d={d} className="fill-none stroke-transparent" strokeWidth={HIT_WIDTH} pointerEvents="stroke" data-testid="canvas-edge-hit" onClick={() => { onSelect(edge); }} />
            )}
            {chosen && editable && edge.key !== null ? <Grip at={targetPoint(to)} bindings={grip(edge)} /> : null}
          </g>
        );
      })}
      {preview ? (
        <path d={edgePath(preview.from, preview.to)} className="fill-none stroke-primary-ink" strokeWidth={2} strokeDasharray={DASH} markerEnd={head("active")} data-testid="canvas-edge-preview" />
      ) : null}
    </svg>
  );
}

/** The round grip at the target end of the selected connector: drag it to another path to reconnect. */
function Grip({ at, bindings }: { at: Point; bindings: PointerBindings }) {
  const center = { x: at.x - GRIP_GAP, y: at.y };
  return (
    <g data-testid="canvas-edge-grip" pointerEvents="all" className="cursor-grab touch-none" {...bindings}>
      <circle cx={center.x} cy={center.y} r={GRIP_RADIUS * 2} className="fill-transparent" />
      <circle cx={center.x} cy={center.y} r={GRIP_RADIUS} className="fill-surface stroke-primary-ink" strokeWidth={2} />
    </g>
  );
}
